import { TriangleAlert } from 'lucide-react';

import { PushUsersState } from './usePushUsers';
import { pushReachOf } from './pushRecipients';

type Translate = (key: string, values?: Record<string, string>) => string;

const T = 'settingsPanel.settingsPanel.alertManagementPanel.push.';

/**
 * Warns when a push action reaches nobody: nobody of the chosen roles and people has push active, or
 * the chosen roles have no members. As long as at least one person is reachable it shows nothing;
 * the per-person state sits in the recipient picker.
 *
 * The wording follows what the server does: a push action never sends e-mail or SMS; somebody without
 * an active device gets nothing from it.
 */
export default function AlertPushReachability(properties: {
    t: Translate;
    state: PushUsersState;
    recipientRoles: string[];
    recipientUsers: string[];
}) {
    const { t, state } = properties;

    if (properties.recipientRoles.length + properties.recipientUsers.length === 0) {
        return null;
    }
    if (state.status !== 'ready') {
        return null;
    }

    const reach = pushReachOf(state.users, properties.recipientRoles, properties.recipientUsers);
    if (reach.reachable > 0) {
        return null;
    }
    const addressed = reach.reachable + reach.unreachable;

    return (
        <p
            role="alert"
            data-testid="push-reach-nobody"
            className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-sm"
        >
            <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>{addressed === 0 ? t(`${T}reachNobodyAddressed`) : t(`${T}reachNobody`)}</span>
        </p>
    );
}
